import { useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { getViewAs } from '@/lib/view-as'

export type TableStatus = 'lobby' | 'playing' | 'over' | 'closed'
export interface GameInfo {
  id: string
  name: string
  emoji: string
  blurb: string
  min: number
  max: number
}
export interface Outcome {
  winners: number[]
  how: string
}
export interface TableSummary {
  host: string
  type: string
  status: TableStatus
  players: string[]
  gone: string[]
  seq: number
  updatedAt: number
  outcome: Outcome | null
  myTurn: boolean
  min: number
  max: number
}
export interface TableDetail extends TableSummary {
  seat: number | null
  startedAt: number | null
  game: GameInfo
  waiting: number[]
  /** What this person may see of the game. The shape depends on the game; the board knows it. */
  view: unknown
}
export interface GamesList {
  games: GameInfo[]
  tables: TableSummary[]
  fetchedAt: number
}
export interface TableLoaded {
  table: TableDetail
  /** Server clock minus this browser's clock, so a countdown matches the server's. */
  skew: number
}

export const gameKeys = {
  list: ['games', 'list'] as const,
  table: (host: string) => ['games', 'table', host] as const,
}

/** Sketches are sent once per version; this keeps the last one so an unchanged poll still has it. */
const sketchCache = new Map<string, { ver: number; strokes: unknown }>()

export function useGamesList(fast: boolean) {
  return useQuery({
    queryKey: gameKeys.list,
    queryFn: async (): Promise<GamesList> => ({ ...(await apiGet<Omit<GamesList, 'fetchedAt'> & { now: number }>('games')), fetchedAt: Date.now() }),
    // Viewing as someone else is refused by the API; do not ask.
    enabled: getViewAs() === null,
    refetchInterval: fast ? 3_000 : 10_000,
    refetchOnWindowFocus: true,
    staleTime: 1_500,
    placeholderData: keepPreviousData,
  })
}

export function useTable(host: string | null, intervalMs = 1_500) {
  return useQuery({
    queryKey: gameKeys.table(host ?? ''),
    enabled: host !== null && getViewAs() === null,
    // A table that has closed answers 404; stop asking rather than poll a dead table.
    refetchInterval: (q) => (q.state.status === 'error' ? false : intervalMs),
    staleTime: 500,
    retry: false,
    queryFn: async (): Promise<TableLoaded> => {
      const id = host as string
      const have = sketchCache.get(id)
      const res = await apiGet<{ now: number; table: TableDetail }>(`games/${id}`, have ? { ver: have.ver } : undefined)
      const view = res.table.view as { ver?: number; strokes?: unknown } | null
      if (view && typeof view.ver === 'number' && 'strokes' in view) {
        if (view.strokes === null && have && have.ver === view.ver) view.strokes = have.strokes
        else sketchCache.set(id, { ver: view.ver, strokes: view.strokes })
      } else sketchCache.delete(id)
      return { table: res.table, skew: res.now - Date.now() }
    },
  })
}

/** Runs a table action, then refreshes what is on screen straight away rather than at the next poll. */
function useAction<V>(host: string | null, run: (host: string, vars: V) => Promise<unknown>, quiet = false) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: V) => run(host as string, vars),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: gameKeys.list })
      if (host) void queryClient.invalidateQueries({ queryKey: gameKeys.table(host) })
    },
    onError: (error) => {
      if (!quiet) toast.error(getErrorMessage(error))
    },
  })
}

export const useOpenTable = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (type: string) => apiPost<{ host: string }>('games', { type }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: gameKeys.list }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
export const useJoin = (host: string | null) => useAction<void>(host, (h) => apiPost(`games/${h}/join`))
export const useLeave = (host: string | null) => useAction<void>(host, (h) => apiPost(`games/${h}/leave`))
export const useStart = (host: string | null) => useAction<void>(host, (h) => apiPost(`games/${h}/start`))
export const useRematch = (host: string | null) => useAction<void>(host, (h) => apiPost(`games/${h}/rematch`))
export const useCloseTable = (host: string | null) => useAction<void>(host, (h) => apiPost(`games/${h}/close`))
export const useMove = (host: string | null) => useAction<Record<string, unknown>>(host, (h, action) => apiPost(`games/${h}/move`, { action }))

/** Wall-clock time, outside any component so event handlers and timers may use it. */
export const stamp = () => Date.now()

/** A wall clock that ticks, for countdowns. State is set from the timer, never read from the clock during render. */
export function useTicker(everyMs: number): number {
  const [now, setNow] = useState(stamp)
  useEffect(() => {
    const id = window.setInterval(() => setNow(stamp()), everyMs)
    return () => window.clearInterval(id)
  }, [everyMs])
  return now
}
